import type { RangeValue, FilterValues } from "@/services/filter";
import { QUERY_FILTER_SLUG, SORT_FILTER_SLUG } from "@/services/filter/filterHelpers";
import type { UrlFilterConfig } from "./types";
import { buildFilterPath, parseFilterPath } from "./filterPathCodec";
import { useRoute, useRouter, type LocationQueryRaw } from "vue-router";

const DEFAULT_CONFIG: Required<Omit<UrlFilterConfig, "filterDefinitions">> = {
  excludeKeys: [],
  pageKey: "page",
  resetPageOnChange: true,
};

// "query" (fulltext) and "sort" are not real elastic filter parameters — they stay in the query
// string (see project decision notes), never in the filter path. Identical across every section
// that uses useUrlFilter today, so this is intentionally a fixed constant, not a config option.
const QUERY_STRING_KEYS = [QUERY_FILTER_SLUG, SORT_FILTER_SLUG];

const FILTER_PATH_PARAM = "filterPath";

/**
 * Generic composable for managing filter state via the URL — category and elastic filter
 * parameters live in the path (see filterPathCodec.ts), "query"/"sort"/page stay in the query
 * string. URL is the source of truth. Filter changes use router.push() so the back button can
 * step through filter states; page changes use router.replace() to avoid spamming history while
 * paginating/loading more (each filter mutation is already debounced upstream by its UI field).
 */
export function useUrlFilter(config: UrlFilterConfig = {}) {
  const route = useRoute();
  const router = useRouter();

  const { excludeKeys, pageKey, resetPageOnChange } = { ...DEFAULT_CONFIG, ...config };
  const filterDefinitions = config.filterDefinitions ?? ref([]);

  const isQueryStringKey = (key: string) => key === pageKey || QUERY_STRING_KEYS.includes(key);

  const filterPathSegments = computed<string[]>(() => {
    const raw = route.params[FILTER_PATH_PARAM];
    return Array.isArray(raw) ? raw : raw ? [raw] : [];
  });

  // Reactive filter state parsed from the URL (path + query), type-agnostic.
  const filterValues = computed<FilterValues>(() => {
    const values: FilterValues = { ...parseFilterPath(filterPathSegments.value) };

    for (const [key, value] of Object.entries(route.query)) {
      if (key === pageKey || !isQueryStringKey(key)) continue;
      if (excludeKeys.includes(key)) continue;

      if (Array.isArray(value)) {
        values[key] = value.filter((v): v is string => v !== null);
      } else if (value !== null) {
        values[key] = value;
      }
    }

    for (const key of excludeKeys) delete values[key];

    return values;
  });

  // Current page from URL (0-indexed)
  const page = computed(() => {
    const p = route.query[pageKey];
    return typeof p === "string" ? parseInt(p, 10) || 0 : 0;
  });

  // Applies a batch of field->value changes, routing each to the path or the query string, then
  // performs a single navigation so path and query always update together.
  const applyChanges = (changes: Record<string, string | string[] | null>) => {
    const nextPathValues: FilterValues = { ...parseFilterPath(filterPathSegments.value) };
    const nextQuery: LocationQueryRaw = { ...route.query };

    for (const [field, value] of Object.entries(changes)) {
      const isEmpty = value === null || value === "" || (Array.isArray(value) && value.length === 0);

      if (isQueryStringKey(field)) {
        if (isEmpty) delete nextQuery[field];
        else nextQuery[field] = value;
      } else {
        if (isEmpty) delete nextPathValues[field];
        else nextPathValues[field] = value;
      }
    }

    if (resetPageOnChange) delete nextQuery[pageKey];

    router.push({
      name: route.name ?? undefined,
      params: { ...route.params, [FILTER_PATH_PARAM]: buildFilterPath(nextPathValues, filterDefinitions.value) },
      query: nextQuery,
    });
  };

  const setFilter = (field: string, value: string | string[] | null) => applyChanges({ [field]: value });

  const setFilters = (filters: Record<string, string | string[] | null>) => applyChanges(filters);

  const setRangeFilter = (field: string, range: RangeValue) =>
    applyChanges({
      [`${field}_min`]: range.min !== undefined ? String(range.min) : null,
      [`${field}_max`]: range.max !== undefined ? String(range.max) : null,
    });

  // Set page (doesn't reset filters, 0-indexed)
  const setPage = (newPage: number) => {
    const nextQuery = { ...route.query };
    if (newPage === 0) {
      delete nextQuery[pageKey];
    } else {
      nextQuery[pageKey] = String(newPage);
    }
    router.replace({ name: route.name ?? undefined, params: route.params, query: nextQuery });
  };

  // Reset filters (path + query string), optionally preserving a set of keys (e.g. category).
  const resetFilters = (preserveKeys: string[] = []) => {
    const currentValues = filterValues.value;
    const preservedPathValues: FilterValues = {};
    const preservedQuery: LocationQueryRaw = {};

    for (const key of preserveKeys) {
      const value = currentValues[key];
      if (value === undefined) continue;
      if (isQueryStringKey(key)) preservedQuery[key] = value;
      else preservedPathValues[key] = value;
    }

    router.push({
      name: route.name ?? undefined,
      params: { ...route.params, [FILTER_PATH_PARAM]: buildFilterPath(preservedPathValues, filterDefinitions.value) },
      query: preservedQuery,
    });
  };

  return {
    filterValues,
    page,
    setFilter,
    setFilters,
    setRangeFilter,
    setPage,
    resetFilters,
  };
}
